import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export const PROCESSED_ORDER_PDF_CAPTURE_CLASS = "processed-order-pdf-capture-mode";

/** Matches the off-screen capture width in CSS (A4-ish at ~96dpi). */
const CAPTURE_WINDOW_WIDTH_PX = 794;

function waitForFonts() {
  if (typeof document === "undefined" || !document.fonts?.ready) {
    return Promise.resolve();
  }
  return document.fonts.ready.catch(() => {});
}

/**
 * Ensures each <img> is decoded and laid out before html2canvas runs.
 * Data URLs (e.g. qrCode.image) often report complete=true before decode finishes; skipping
 * that step is a common cause of missing images in the rasterized PDF.
 */
async function waitForImages(root) {
  if (!root) return;
  const imgs = [...root.querySelectorAll("img")];
  await Promise.all(imgs.map(waitForSingleImageReady));
}

async function waitForSingleImageReady(img) {
  const src = img.currentSrc || img.getAttribute("src") || "";
  if (!src) return;

  if (!img.complete) {
    await new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    });
  }

  if (typeof img.decode === "function") {
    try {
      await img.decode();
    } catch (_e) {
      /* broken image — still let html2canvas run */
    }
  }

  for (let i = 0; i < 60; i++) {
    if (img.naturalWidth > 0 || img.naturalHeight > 0) break;
    await new Promise((r) => requestAnimationFrame(r));
  }
}

function nextFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

/**
 * html2canvas reloads images inside a cloned tree; very long `data:image/...;base64,...` URLs are
 * handled inconsistently in production (minified bundles, stricter timing). Rebiding the same
 * pixels to a short-lived `blob:` URL matches the loader path used for http(s) assets and fixes
 * missing QR bitmaps on some deployed hosts while keeping local dev behaviour.
 *
 * @returns {() => void} call after capture to revoke blob URLs and restore original `src`
 */
async function rebindDataUrlImagesToBlobUrls(root) {
  const restored = [];

  for (const img of root.querySelectorAll("img")) {
    const dataSrc = img.getAttribute("src") || "";
    if (!dataSrc.startsWith("data:image")) continue;
    try {
      const res = await fetch(dataSrc);
      if (!res.ok) continue;
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      img.src = blobUrl;
      await waitForSingleImageReady(img);
      restored.push({ img, dataSrc, blobUrl });
    } catch (_e) {
      /* keep original data URL */
    }
  }

  return () => {
    restored.forEach(({ img, dataSrc, blobUrl }) => {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch (_e) {
        /* ignore */
      }
      if (img && dataSrc != null) {
        img.setAttribute("src", dataSrc);
      }
    });
  };
}

/**
 * Rasterize long content across multiple A4 pages (single canvas → vertical slice via negative Y).
 */
function addCanvasToPdf(pdf, canvas, marginMm) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth - 2 * marginMm;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const imgData = canvas.toDataURL("image/png");

  let heightLeft = imgHeight;
  let y = marginMm;

  pdf.addImage(imgData, "PNG", marginMm, y, imgWidth, imgHeight);
  heightLeft -= pageHeight - 2 * marginMm;

  while (heightLeft > 0) {
    y = marginMm - (imgHeight - heightLeft);
    pdf.addPage();
    pdf.addImage(imgData, "PNG", marginMm, y, imgWidth, imgHeight);
    heightLeft -= pageHeight - 2 * marginMm;
  }
}

/**
 * @param {HTMLElement} element — root node of ProcessedOrderPrintDocument
 * @param {{ shouldAbort?: () => boolean, preOpenedWindow?: Window | null }} [options]
 * @returns {Promise<{ opened: boolean; blocked?: boolean; aborted?: boolean }>}
 */
export async function generateAndOpenProcessedOrderPdf(element, options = {}) {
  const { shouldAbort, preOpenedWindow } = options;
  if (!element) {
    throw new Error("Missing print root element");
  }

  element.classList.add(PROCESSED_ORDER_PDF_CAPTURE_CLASS);

  let restoreDataUrlRebind = () => {};

  try {
    await waitForFonts();
    await waitForImages(element);
    try {
      restoreDataUrlRebind = await rebindDataUrlImagesToBlobUrls(element);
    } catch (_e) {
      restoreDataUrlRebind = () => {};
    }
    await nextFrame();
    // Layout + decode: deployed builds are often slower than local dev.
    await new Promise((r) => window.setTimeout(r, 120));

    const marginMm = 10;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0,
      windowWidth: CAPTURE_WINDOW_WIDTH_PX,
      imageTimeout: 0,
      onclone: (clonedDoc) => {
        clonedDoc.querySelectorAll("img").forEach((node) => {
          const raw = node.getAttribute("src") || "";
          if (raw.startsWith("data:image") || raw.startsWith("blob:")) {
            node.removeAttribute("crossorigin");
            node.removeAttribute("crossOrigin");
          }
        });
      },
    });

    if (shouldAbort?.()) {
      return { opened: false, aborted: true, blocked: false };
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    addCanvasToPdf(pdf, canvas, marginMm);

    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);

    // 1. Try the pre-opened window (opened synchronously in the user gesture,
    //    before any await — this is the only way iOS Safari doesn't block it).
    if (preOpenedWindow) {
      try {
        preOpenedWindow.location.replace(url);
        window.setTimeout(() => URL.revokeObjectURL(url), 180000);
        return { opened: true };
      } catch (_e) {
        try { preOpenedWindow.close(); } catch (_) {}
      }
    }

    // 2. Standard window.open (works on desktop; blocked on iOS after async work).
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (win) {
      window.setTimeout(() => URL.revokeObjectURL(url), 180000);
      return { opened: true };
    }

    // 3. Last resort: pdf.save() — triggers download dialog on Android,
    //    opens PDF viewer on iOS Safari.
    URL.revokeObjectURL(url);
    try {
      pdf.save("listek.pdf");
      return { opened: true };
    } catch (_e) {
      return { opened: false, blocked: true };
    }
  } finally {
    restoreDataUrlRebind();
    element.classList.remove(PROCESSED_ORDER_PDF_CAPTURE_CLASS);
  }
}
